import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Badge, Button, Form, Spinner, Alert, Table, Tabs, Tab,
} from 'react-bootstrap';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../config/api';
import AppIcon from './AppIcon';
import AddStockToPortfolioModal from './AddStockToPortfolioModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricePoint {
  date: string;
  close: number | null;
  volume: number | null;
}

interface FinancialRow {
  metric: string;
  [col: string]: string | number | null;
}

interface StockDetailData {
  id: number;
  symbol: string;
  name: string;
  isin: string;
  exchange: string;
  last_price: number | null;
  cmp: number | null;
  pe_ratio: number | null;
  peg: number | null;
  eps: number | null;
  market_cap: number | null;
  high_52week: number | null;
  low_52week: number | null;
  sector: string | null;
  industry: string | null;
  website: string | null;
  earnings_growth: number | null;
  description: string | null;
  news: Array<{
    title: string;
    summary: string;
    url: string;
    published: string;
    source: string;
  }>;
  price_history: PricePoint[];
  financials: FinancialRow[];
  balance_sheet: FinancialRow[];
  cashflow: FinancialRow[];
}

type ChartRange = '1d' | '1mo' | '1y' | '3y' | 'max';

const CHART_RANGES: Array<{ value: ChartRange; label: string; title: string }> = [
  { value: '1d', label: '1D', title: '1 Day' },
  { value: '1mo', label: '1M', title: '1 Month' },
  { value: '1y', label: '1Y', title: '1 Year' },
  { value: '3y', label: '3Y', title: '3 Years' },
  { value: 'max', label: 'Max', title: 'Max' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v: number | null | undefined, decimals = 2) =>
  v != null ? `₹${v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}` : 'N/A';

const fmtNum = (v: number | null | undefined, decimals = 2) =>
  v != null ? v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : 'N/A';

const fmtCr = (v: number | null | undefined) => {
  if (v == null) return 'N/A';
  if (Math.abs(v) >= 1e12) return `₹${(v / 1e12).toFixed(2)} T`;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const fmtPct = (v: number | null | undefined) =>
  v != null ? `${(v * 100).toFixed(2)}%` : 'N/A';

const formatChartTick = (value: string) => {
  if (value.includes(' ')) {
    const [, time] = value.split(' ');
    return time;
  }

  const [year, month, day] = value.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (!year || !month || !day) return value;
  return `${parseInt(day)} ${months[parseInt(month)-1]}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border rounded p-2 shadow-sm" style={{ fontSize: 13 }}>
        <div className="text-muted">{label}</div>
        <div className="fw-bold text-primary">₹{payload[0].value?.toFixed(2)}</div>
        {payload[1] && (
          <div className="text-secondary" style={{ fontSize: 11 }}>
            Vol: {payload[1].value?.toLocaleString('en-IN')}
          </div>
        )}
      </div>
    );
  }
  return null;
};

// ─── Financial Table ──────────────────────────────────────────────────────────

const FinTable: React.FC<{ rows: FinancialRow[] }> = ({ rows }) => {
  if (!rows || rows.length === 0)
    return <p className="text-muted p-3 mb-0">No data available.</p>;

  const cols = Object.keys(rows[0]).filter((k) => k !== 'metric');
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped bordered hover size="sm" className="mb-0" style={{ fontSize: 12, minWidth: 500 }}>
        <thead className="table-dark">
          <tr>
            <th style={{ minWidth: 220 }}>Metric</th>
            {cols.map((c) => <th key={c} className="text-end">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.metric}>
              <td className="fw-semibold">{row.metric}</td>
              {cols.map((c) => {
                const v = row[c] as number | null;
                return (
                  <td key={c} className={`text-end ${v != null && v < 0 ? 'text-danger' : ''}`}>
                    {v != null ? fmtCr(v) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StockDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [stock, setStock] = useState<StockDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [showAddToPortfolio, setShowAddToPortfolio] = useState(false);
  const [chartRange, setChartRange] = useState<ChartRange>('1mo');
  const loadedStockId = useRef<string | undefined>(undefined);

  useEffect(() => {
    const fetch = async () => {
      try {
        if (loadedStockId.current === id) {
          setChartLoading(true);
        } else {
          setLoading(true);
        }
        const res = await axios.get(`${API_BASE_URL}/stocks/${id}/`, {
          params: { range: chartRange },
        });
        setStock(res.data);
        loadedStockId.current = id;
      } catch {
        setError('Failed to load stock details.');
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    };
    fetch();
  }, [id, chartRange]);

  // Thin price history to ≤120 points for chart performance
  const chartData = useMemo(() => {
    if (!stock?.price_history) return [];
    const h = stock.price_history;
    if (h.length <= 120) return h;
    const step = Math.floor(h.length / 120);
    return h.filter((_, i) => i % step === 0 || i === h.length - 1);
  }, [stock]);

  const priceChange = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].close;
    const last = chartData[chartData.length - 1].close;
    if (first == null || last == null || first === 0) return null;
    return { abs: last - first, pct: ((last - first) / first) * 100 };
  }, [chartData]);

  // 52-week range percentage fill
  const rangePos = useMemo(() => {
    if (!stock?.cmp || !stock.low_52week || !stock.high_52week) return null;
    const range = stock.high_52week - stock.low_52week;
    if (range === 0) return null;
    return Math.min(100, Math.max(0, ((stock.cmp - stock.low_52week) / range) * 100));
  }, [stock]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error && !stock) return <Alert variant="danger">{error}</Alert>;
  if (!stock) return null;

  const livePrice = stock.cmp ?? stock.last_price;
  const selectedRange = CHART_RANGES.find((range) => range.value === chartRange) ?? CHART_RANGES[1];

  return (
    <div className="instrument-detail-page instrument-detail-page--stock">
      {/* Back */}
      <Button variant="outline-secondary" size="sm" className="instrument-back-button mb-3" onClick={() => navigate('/stocks')}>
        <AppIcon name="arrow-left" /> Back to Stocks
      </Button>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {addSuccess && <Alert variant="success" dismissible onClose={() => setAddSuccess('')}>{addSuccess}</Alert>}

      {/* ── Header Card ── */}
      <Card className="instrument-hero-card mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-start">
            <Col md={8}>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h4 className="fw-bold mb-0">{stock.symbol}</h4>
                {stock.exchange && <Badge bg="dark">{stock.exchange}</Badge>}
              </div>
              <div className="text-muted mb-2" style={{ fontSize: 15 }}>{stock.name}</div>
              <div className="d-flex flex-wrap gap-2">
                {stock.sector && <Badge bg="secondary">{stock.sector}</Badge>}
                {stock.industry && <Badge bg="info" text="dark">{stock.industry}</Badge>}
              </div>
              {stock.website && (
                <div className="mt-2" style={{ fontSize: 13 }}>
                  <a href={stock.website} target="_blank" rel="noreferrer" className="text-decoration-none">
                    🌐 {stock.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <div className="text-muted" style={{ fontSize: 13 }}>Current Price</div>
              <div className="fw-bold" style={{ fontSize: 28 }}>{fmt(livePrice)}</div>
              {priceChange && (
                <div className={priceChange.pct >= 0 ? 'text-success' : 'text-danger'} style={{ fontSize: 14 }}>
                  {priceChange.pct >= 0 ? '▲' : '▼'} {Math.abs(priceChange.pct).toFixed(2)}% ({selectedRange.label})
                </div>
              )}
              <Button
                variant="primary"
                size="sm"
                className="mt-2"
                onClick={() => setShowAddToPortfolio(true)}
              >
                + Add to Portfolio
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <AddStockToPortfolioModal
        show={showAddToPortfolio}
        stock={stock}
        onHide={() => setShowAddToPortfolio(false)}
        onSuccess={(addedStock) => setAddSuccess(`${addedStock.symbol} was added to your portfolio.`)}
      />

      {/* ── Key Metrics ── */}
      <Row className="mb-4 g-3">
        {[
          { label: 'CMP', value: fmt(stock.cmp) },
          { label: 'P/E Ratio', value: fmtNum(stock.pe_ratio) },
          { label: 'PEG Ratio', value: fmtNum(stock.peg) },
          { label: 'EPS (TTM)', value: fmt(stock.eps) },
          { label: 'Market Cap', value: fmtCr(stock.market_cap) },
          { label: 'Earnings Growth', value: fmtPct(stock.earnings_growth) },
          { label: '52W High', value: fmt(stock.high_52week) },
          { label: '52W Low', value: fmt(stock.low_52week) },
        ].map(({ label, value }) => (
          <Col xs={6} md={3} key={label}>
            <Card className="instrument-metric-card h-100 shadow-sm text-center">
              <Card.Body className="py-2 px-3">
                <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
                <div className="fw-bold" style={{ fontSize: 16 }}>{value}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── 52-Week Range Bar ── */}
      {rangePos !== null && (
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
              <span className="text-danger">52W Low: {fmt(stock.low_52week)}</span>
              <span className="fw-semibold">CMP: {fmt(stock.cmp)}</span>
              <span className="text-success">52W High: {fmt(stock.high_52week)}</span>
            </div>
            <div className="progress" style={{ height: 10, borderRadius: 5 }}>
              <div
                className="progress-bar bg-primary"
                style={{ width: `${rangePos}%`, borderRadius: 5 }}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── ISIN / Symbol info ── */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="instrument-metric-card shadow-sm h-100">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 12 }}>Symbol</div>
              <div className="fw-bold">{stock.symbol}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="instrument-metric-card shadow-sm h-100">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 12 }}>ISIN</div>
              <code>{stock.isin || 'N/A'}</code>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="instrument-metric-card shadow-sm h-100">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 12 }}>Exchange</div>
              <div className="fw-bold">{stock.exchange || 'N/A'}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Price Chart ── */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">Price History</h5>
          <Form.Select
            size="sm"
            style={{ width: 'auto', minWidth: 130 }}
            value={chartRange}
            onChange={(e) => setChartRange(e.target.value as ChartRange)}
            disabled={chartLoading}
          >
            {CHART_RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.title}</option>
            ))}
          </Form.Select>
        </Card.Header>
        <Card.Body className="position-relative">
          {chartLoading && (
            <div className="position-absolute top-0 end-0 m-3 text-primary d-flex align-items-center gap-2" style={{ fontSize: 13, zIndex: 1 }}>
              <Spinner size="sm" animation="border" />
              Updating
            </div>
          )}
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={300} style={{ opacity: chartLoading ? 0.55 : 1 }}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6c63ff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6c63ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  tickFormatter={formatChartTick}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `₹${v}`}
                  width={75}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="close"
                  stroke="#6c63ff"
                  fill="url(#priceGrad)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-muted py-4">No price history available.</div>
          )}
        </Card.Body>
      </Card>

      {/* ── About & News ── */}
      {(stock.description || stock.news.length > 0) && (
        <Card className="mb-4 shadow-sm">
          <Card.Header>
            <h5 className="mb-0">About {stock.name}</h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-4">
              {stock.description && (
                <Col xs={12} md={stock.news.length > 0 ? 5 : 12}>
                  <div className="text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Company Overview</div>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: '#444' }}>{stock.description}</p>
                </Col>
              )}
              {stock.news.length > 0 && (
                <Col xs={12} md={stock.description ? 7 : 12}>
                  <div className="text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Recent News</div>
                  <div className="d-flex flex-column gap-2">
                    {stock.news.map((item, i) => (
                      <div key={i} className="border rounded p-2" style={{ fontSize: 13 }}>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="fw-semibold text-decoration-none text-dark"
                        >
                          {item.title}
                        </a>
                        {item.summary && (
                          <p className="text-muted mb-1 mt-1" style={{ fontSize: 12 }}>{item.summary}</p>
                        )}
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {item.source && <span>{item.source}</span>}
                          {item.source && item.published && <span> · </span>}
                          {item.published && <span>{new Date(item.published).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Financial Tables ── */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Financials</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Tabs defaultActiveKey="income" className="px-3 pt-2 border-bottom">
            <Tab eventKey="income" title="Income Statement">
              <div className="p-0">
                <FinTable rows={stock.financials} />
              </div>
            </Tab>
            <Tab eventKey="balance" title="Balance Sheet">
              <div className="p-0">
                <FinTable rows={stock.balance_sheet} />
              </div>
            </Tab>
            <Tab eventKey="cashflow" title="Cash Flow">
              <div className="p-0">
                <FinTable rows={stock.cashflow} />
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default StockDetail;
